'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import {
  Shield,
  DollarSign,
  Banknote,
  TrendingUp,
  Home,
  PiggyBank,
  Info,
} from 'lucide-react'
import {
  calculateDeploymentSavings,
  type DeploymentInput,
  type DeploymentResult,
} from '@/app/actions'

/* ── Constants ─────────────────────────────────────────── */
const RANKS = [
  'E-1','E-2','E-3','E-4','E-5','E-6','E-7','E-8','E-9',
  'W-1','W-2','W-3','W-4','W-5',
  'O-1','O-2','O-3','O-4','O-5','O-6','O-7','O-8','O-9','O-10',
]
const YOS = Array.from({ length: 31 }, (_, i) => i)

/* ── Animated number ──────────────────────────────────── */
function AnimNum({ value, prefix = '$' }: { value: number; prefix?: string }) {
  const mv = useMotionValue(0)
  const display = useTransform(mv, (v) => `${prefix}${Math.round(v).toLocaleString()}`)
  const [text, setText] = useState(`${prefix}0`)
  useEffect(() => { const u = display.on('change', setText); return u }, [display])
  useEffect(() => { const c = animate(mv, value, { duration: 1.2, ease: [0.16, 1, 0.3, 1] }); return () => c.stop() }, [value, mv])
  return <span>{text}</span>
}

/* ── Stacked bar chart ────────────────────────────────── */
function SavingsChart({ result }: { result: DeploymentResult }) {
  const { months } = result
  if (months.length === 0) return null

  const W = 700, H = 320
  const PAD = { top: 40, right: 20, bottom: 40, left: 70 }
  const cw = W - PAD.left - PAD.right
  const ch = H - PAD.top - PAD.bottom

  // Categories to stack
  const categories = [
    { key: 'basePay', color: '#6366f1', label: 'Base Pay' },
    { key: 'specialPay', color: '#f59e0b', label: 'Special Pay' },
    { key: 'bah', color: '#3b82f6', label: 'BAH' },
    { key: 'taxSavings', color: '#10b981', label: 'Tax Savings' },
    { key: 'expenseSavings', color: '#14b8a6', label: 'Expense Savings' },
    { key: 'sdpInterest', color: '#ec4899', label: 'SDP Interest' },
  ] as const

  // Total per month for y-axis max
  const monthTotals = months.map((m) =>
    m.basePay + m.specialPay + m.bah + m.taxSavings + m.expenseSavings + m.sdpInterest
  )
  const maxTotal = Math.max(...monthTotals, 1)

  const barW = Math.min(40, (cw / months.length) * 0.7)
  const gap = (cw - barW * months.length) / (months.length + 1)

  const x = (i: number) => PAD.left + gap + i * (barW + gap)
  const yScale = (v: number) => (v / maxTotal) * ch

  // Y-axis ticks
  const yTicks = 5
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((maxTotal / yTicks) * i))

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* Grid */}
      {yLabels.map((val) => (
        <g key={val}>
          <line
            x1={PAD.left} x2={W - PAD.right}
            y1={PAD.top + ch - yScale(val)} y2={PAD.top + ch - yScale(val)}
            stroke="var(--border)" strokeWidth={0.5} strokeDasharray="4 4"
          />
          <text x={PAD.left - 8} y={PAD.top + ch - yScale(val) + 3} textAnchor="end" fill="var(--text-3)" fontSize={10}>
            ${(val / 1000).toFixed(1)}k
          </text>
        </g>
      ))}

      {/* Stacked bars */}
      {months.map((m, i) => {
        let yOffset = 0
        return (
          <g key={m.month}>
            {categories.map((cat) => {
              const val = m[cat.key] as number
              if (val <= 0) return null
              const barH = yScale(val)
              const yPos = PAD.top + ch - yOffset - barH
              yOffset += barH
              return (
                <rect
                  key={cat.key}
                  x={x(i)}
                  y={yPos}
                  width={barW}
                  height={barH}
                  rx={2}
                  fill={cat.color}
                  opacity={0.85}
                />
              )
            })}
            {/* Month label */}
            <text
              x={x(i) + barW / 2}
              y={H - 8}
              textAnchor="middle"
              fill="var(--text-3)"
              fontSize={10}
            >
              {m.month}
            </text>
          </g>
        )
      })}

      {/* Legend — above chart area */}
      {(() => {
        const visible = categories.filter(c => months.some(m => (m[c.key] as number) > 0))
        const itemW = (W - PAD.left - PAD.right) / Math.max(visible.length, 1)
        return (
          <g transform={`translate(${PAD.left}, 12)`}>
            {visible.map((cat, i) => (
              <g key={cat.key} transform={`translate(${i * itemW}, 0)`}>
                <rect width={8} height={8} rx={2} fill={cat.color} opacity={0.85} />
                <text x={12} y={8} fill="var(--text-2)" fontSize={9} fontWeight={500}>{cat.label}</text>
              </g>
            ))}
          </g>
        )
      })()}
    </svg>
  )
}

/* ── Cumulative line ──────────────────────────────────── */
function CumulativeLine({ result }: { result: DeploymentResult }) {
  const { months } = result
  if (months.length === 0) return null

  const W = 700, H = 180
  const PAD = { top: 20, right: 20, bottom: 30, left: 70 }
  const cw = W - PAD.left - PAD.right
  const ch = H - PAD.top - PAD.bottom

  const maxVal = Math.max(...months.map(m => m.totalSaved), 1)
  const x = (i: number) => PAD.left + (i / (months.length - 1 || 1)) * cw
  const y = (v: number) => PAD.top + ch - (v / maxVal) * ch

  const line = months.map((m, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(m.totalSaved)}`).join(' ')
  const area = `${line} L${x(months.length - 1)},${y(0)} L${x(0)},${y(0)} Z`

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* Grid */}
      {[0, 0.25, 0.5, 0.75, 1].map(pct => {
        const val = Math.round(maxVal * pct)
        return (
          <g key={pct}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(val)} y2={y(val)} stroke="var(--border)" strokeWidth={0.5} strokeDasharray="4 4" />
            <text x={PAD.left - 8} y={y(val) + 3} textAnchor="end" fill="var(--text-3)" fontSize={10}>${(val / 1000).toFixed(0)}k</text>
          </g>
        )
      })}
      <path d={area} fill="#10b981" opacity={0.1} />
      <path d={line} fill="none" stroke="#10b981" strokeWidth={2.5} />
      <circle cx={x(months.length - 1)} cy={y(months[months.length - 1].totalSaved)} r={4} fill="#10b981" />
      {/* Final label */}
      <text
        x={x(months.length - 1)}
        y={y(months[months.length - 1].totalSaved) - 10}
        textAnchor="middle"
        fill="#10b981"
        fontSize={12}
        fontWeight="bold"
      >
        ${(months[months.length - 1].totalSaved / 1000).toFixed(1)}k
      </text>
    </svg>
  )
}

/* ── Toggle ───────────────────────────────────────────── */
function Toggle({ value, onChange, label, sub }: { value: boolean; onChange: (v: boolean) => void; label: string; sub?: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-[var(--surface-2)] px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-[var(--text-1)]">{label}</p>
        {sub && <p className="text-xs text-[var(--text-3)]">{sub}</p>}
      </div>
      <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            onClick={() => onChange(v)}
            className={`px-3 py-1.5 text-xs font-semibold transition ${
              value === v
                ? 'bg-blue-600 text-white'
                : 'bg-[var(--surface-1)] text-[var(--text-3)] hover:text-[var(--text-1)]'
            }`}
          >
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ── Main Component ───────────────────────────────────── */
export default function DeploymentSavings() {
  const [rank, setRank] = useState('E-4')
  const [yos, setYos] = useState(2)
  const [deploymentMonths, setDeploymentMonths] = useState(9)
  const [hasDependents, setHasDependents] = useState(false)
  const [receivesBAH, setReceivesBAH] = useState(false)
  const [bahZip, setBahZip] = useState('')
  const [czte, setCzte] = useState(true)
  const [czteMonths, setCzteMonths] = useState(9) // defaults to full deployment
  const [hfp, setHfp] = useState(true)
  const [fsa, setFsa] = useState(false)
  const [hdp, setHdp] = useState(true)
  const [useSDP, setUseSDP] = useState(true)
  const [sdpMonthly, setSdpMonthly] = useState(833)
  const [normalExpenses, setNormalExpenses] = useState(2000)
  const [deployedExpenses, setDeployedExpenses] = useState(300)

  const [result, setResult] = useState<DeploymentResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Keep CZTE months capped at deployment length
  useEffect(() => { setCzteMonths((prev) => Math.min(prev, deploymentMonths)) }, [deploymentMonths])
  // Auto-set FSA when dependents toggled
  useEffect(() => { setFsa(hasDependents) }, [hasDependents])
  // Auto-set BAH when dependents toggled
  useEffect(() => { if (hasDependents) setReceivesBAH(true) }, [hasDependents])

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await calculateDeploymentSavings({
        rank,
        yearsOfService: yos,
        deploymentMonths,
        hasDependents,
        receivesBAH,
        bahZip,
        combatZoneTaxExclusion: czte,
        czteMonths: czte ? czteMonths : 0,
        hostileFirePay: hfp,
        familySeparation: fsa,
        hardshipDutyPay: hdp,
        useSDP,
        sdpMonthlyDeposit: sdpMonthly,
        normalMonthlyExpenses: normalExpenses,
        deployedMonthlyExpenses: deployedExpenses,
      })
      setResult(res)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }, [rank, yos, deploymentMonths, hasDependents, receivesBAH, bahZip, czte, czteMonths, hfp, fsa, hdp, useSDP, sdpMonthly, normalExpenses, deployedExpenses])

  useEffect(() => {
    const t = setTimeout(run, 400)
    return () => clearTimeout(t)
  }, [run])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/10">
          <Shield className="h-5 w-5 text-[var(--accent)]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--text-1)]">Deployment Savings Calculator</h1>
          <p className="text-sm text-[var(--text-3)]">See how much you can save on a combat deployment</p>
        </div>
      </div>

      {/* ── Inputs ─────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm space-y-6">
        {/* Row 1: Rank, YOS, Deployment length */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Rank</label>
            <select
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
            >
              {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Years of Service</label>
            <select
              value={yos}
              onChange={(e) => setYos(Number(e.target.value))}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
            >
              {YOS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Deployment Length</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={1} max={18}
                value={deploymentMonths}
                onChange={(e) => setDeploymentMonths(Number(e.target.value))}
                className="flex-1 accent-blue-600"
              />
              <span className="w-14 text-right text-sm font-bold text-[var(--text-1)]">{deploymentMonths} mo</span>
            </div>
          </div>
        </div>

        {/* Dependents + BAH */}
        <div className="grid grid-cols-2 gap-4">
          <Toggle value={hasDependents} onChange={setHasDependents} label="Dependents" sub="Affects FSA + BAH eligibility" />
          <div className="space-y-2">
            <Toggle value={receivesBAH} onChange={setReceivesBAH} label="Receives BAH" sub="BAH continues during deployment" />
            {receivesBAH && (
              <div className="pl-4">
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Home ZIP (for BAH rate)</label>
                <input
                  type="text"
                  value={bahZip}
                  onChange={(e) => setBahZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="e.g. 28540"
                  maxLength={5}
                  className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-1.5 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
                />
              </div>
            )}
          </div>
        </div>

        {/* Pay toggles */}
        <div>
          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Deployment Pay & Benefits</label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Toggle value={czte} onChange={setCzte} label="Combat Zone Tax Exclusion" sub="Tax-free base pay" />
              {czte && (
                <div className="rounded-lg bg-[var(--surface-2)] px-4 py-2.5">
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">
                    Months in CZTE Zone
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={1}
                      max={deploymentMonths}
                      value={czteMonths}
                      onChange={(e) => setCzteMonths(Number(e.target.value))}
                      className="flex-1 accent-blue-600"
                    />
                    <span className="w-16 text-right text-sm font-bold text-[var(--text-1)]">
                      {czteMonths}/{deploymentMonths}
                    </span>
                  </div>
                  {czteMonths < deploymentMonths && (
                    <p className="mt-1 text-[10px] text-[var(--text-3)]">
                      Tax-free for {czteMonths} of {deploymentMonths} months
                    </p>
                  )}
                </div>
              )}
            </div>
            <Toggle value={hfp} onChange={setHfp} label="Hostile Fire Pay" sub="$225/mo" />
            <Toggle value={hdp} onChange={setHdp} label="Hardship Duty Pay" sub="$150/mo" />
            <Toggle value={fsa} onChange={setFsa} label="Family Separation" sub={hasDependents ? '$250/mo' : 'Requires dependents'} />
          </div>
        </div>

        {/* SDP */}
        <div className="space-y-3">
          <Toggle value={useSDP} onChange={setUseSDP} label="Savings Deposit Program (SDP)" sub="10% guaranteed annual return, up to $10,000" />
          {useSDP && (
            <div className="grid grid-cols-2 gap-4 pl-4">
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Monthly SDP Deposit</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--text-3)]">$</span>
                  <input
                    type="number"
                    value={sdpMonthly || ''}
                    onChange={(e) => setSdpMonthly(Number(e.target.value) || 0)}
                    placeholder="833"
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] pl-7 pr-3 py-1.5 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <p className="text-xs text-[var(--text-3)]">
                  At ${sdpMonthly}/mo you&apos;ll hit the $10k cap in ~{Math.ceil(10000 / (sdpMonthly || 1))} months
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Expense comparison */}
        <div>
          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Monthly Expenses</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[10px] font-semibold text-[var(--text-3)]">Stateside (normal)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--text-3)]">$</span>
                <input
                  type="number"
                  value={normalExpenses || ''}
                  onChange={(e) => setNormalExpenses(Number(e.target.value) || 0)}
                  className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] pl-7 pr-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold text-[var(--text-3)]">Deployed</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--text-3)]">$</span>
                <input
                  type="number"
                  value={deployedExpenses || ''}
                  onChange={(e) => setDeployedExpenses(Number(e.target.value) || 0)}
                  className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] pl-7 pr-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
                />
              </div>
            </div>
          </div>
          {normalExpenses > deployedExpenses && (
            <p className="mt-2 text-xs font-semibold text-teal-500">
              Saving ${(normalExpenses - deployedExpenses).toLocaleString()}/mo in reduced expenses
            </p>
          )}
        </div>
      </div>

      {/* ── Results ────────────────────────────────────────── */}
      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-5 py-3 text-sm text-red-400">{error}</div>
      )}

      {result && !error && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          {/* Hero stat */}
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-500">
              Total Deployment Savings ({deploymentMonths} months)
            </p>
            <p className="mt-2 text-4xl font-bold text-emerald-500">
              <AnimNum value={result.grandTotal} />
            </p>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            {[
              { label: 'Base Pay', value: result.totalBasePay, color: 'text-[var(--accent)]', icon: DollarSign },
              { label: 'Special Pay', value: result.totalSpecialPay, color: 'text-blue-600', icon: Banknote },
              { label: 'BAH', value: result.totalBAH, color: 'text-blue-600', icon: Home },
              { label: 'Tax Saved', value: result.totalTaxSavings, color: 'text-emerald-500', icon: TrendingUp },
              { label: 'Expense Saved', value: result.totalExpenseSavings, color: 'text-emerald-500', icon: PiggyBank },
              { label: 'SDP Interest', value: result.totalSDPInterest, color: 'text-emerald-500', icon: TrendingUp },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-4 shadow-sm">
                <s.icon className={`mb-1 h-4 w-4 ${s.color}`} />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">{s.label}</p>
                <p className={`mt-1 text-base font-bold ${s.color}`}>
                  <AnimNum value={s.value} />
                </p>
              </div>
            ))}
          </div>

          {/* Stacked bar chart */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <h2 className="mb-4 text-sm font-bold text-[var(--text-1)]">Monthly Breakdown</h2>
            <SavingsChart result={result} />
          </div>

          {/* Cumulative line */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <h2 className="mb-4 text-sm font-bold text-[var(--text-1)]">Cumulative Savings</h2>
            <CumulativeLine result={result} />
          </div>

          {/* SDP callout */}
          {useSDP && result.totalSDPInterest > 0 && (
            <div className="flex items-start gap-3 rounded-xl border border-blue-600/20 bg-blue-600/5 px-5 py-4">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-blue-600">
                  ${result.totalSDPInterest.toLocaleString()} earned from SDP at 10% guaranteed return
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-3)]">
                  The Savings Deposit Program lets you deposit up to $10,000 while deployed and earn a guaranteed
                  10% annual return — the best risk-free rate available anywhere.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {loading && !result && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      )}
    </div>
  )
}
